import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Bell,
  Mail,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Send,
  RefreshCw,
  Clock,
} from "lucide-react";

const API = "/api";

/**
 * Admin page to inspect & test the class-reminder email system.
 *
 *   • Shows current scheduler config (windows + Gmail status)
 *   • Lists today + tomorrow's bookings with each student's email
 *   • One-click "Send test reminder" per row (sends a real email immediately)
 *   • Last 10 reminders actually sent (from notifications_sent collection)
 */
export default function AdminReminders() {
  const { getAuthHeader } = useAuth();
  const headers = { headers: getAuthHeader() };

  const [status, setStatus] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [st, sched] = await Promise.all([
        axios.get(`${API}/admin/reminders/status`, headers),
        axios.get(`${API}/schedule/all`, headers),
      ]);
      setStatus(st.data);

      // Filter to today + tomorrow's booked classes
      const today = new Date();
      const todayStr = today.toISOString().slice(0, 10);
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const tomorrowStr = tomorrow.toISOString().slice(0, 10);
      const allSlots = sched.data?.slots || sched.data || [];
      const relevant = allSlots.filter(
        (s) =>
          [todayStr, tomorrowStr].includes(s.date) &&
          s.student_id &&
          ["booked", "pending_tagging"].includes(s.status) &&
          !s.is_trial
      );
      // Sort: today first, then by time
      relevant.sort((a, b) =>
        a.date === b.date
          ? (a.time || "").localeCompare(b.time || "")
          : a.date.localeCompare(b.date)
      );
      setSlots(relevant);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendTest = async (slot) => {
    setSendingId(slot.id);
    try {
      const r = await axios.post(
        `${API}/admin/reminders/send-test/${slot.id}`,
        {},
        headers
      );
      const tutorMsg = r.data.tutor_reminder?.sent
        ? ` + tutor (${r.data.tutor_reminder.to})`
        : r.data.tutor_reminder?.to === null && !r.data.tutor_reminder?.error
          ? " (no tutor assigned)"
          : r.data.tutor_reminder?.error
            ? ` · tutor: ${r.data.tutor_reminder.error}`
            : "";
      toast.success(
        `✅ Sent to ${r.data.sent_to} (${r.data.student_name})${tutorMsg}`,
        { duration: 8000 }
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-bold text-slate-900 flex items-center gap-2"
            data-testid="admin-reminders-title"
          >
            <Bell className="w-7 h-7 text-amber-500" /> Class Reminder Emails
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            <strong>Students AND tutors</strong> automatically receive 3 reminder
            emails per class: 24 hours, 1 hour, and 15 minutes before the start
            time. Use "Send Test" to verify both inboxes immediately.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* System Status */}
      <Card>
        <CardContent className="p-5 space-y-2">
          <p className="font-semibold text-slate-900 flex items-center gap-1.5 mb-2">
            <Mail className="w-4 h-4 text-emerald-600" /> System Status
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              {status?.gmail_configured ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-600" />
              )}
              <span>
                Gmail SMTP:{" "}
                <strong>
                  {status?.gmail_configured ? "Connected" : "NOT configured"}
                </strong>
              </span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4" />
              <span>
                Scheduler checks every{" "}
                <strong>{status?.scheduler_interval_minutes || "—"} min</strong>
              </span>
            </div>
            <div className="text-xs text-slate-500 sm:col-span-2">
              From: <code className="bg-slate-100 px-1 rounded">{status?.gmail_user}</code>{" "}
              · Windows: {status?.windows?.join(" · ")}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming bookings */}
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="font-semibold text-slate-900">
              Upcoming bookings — today &amp; tomorrow
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {slots.length} eligible class{slots.length === 1 ? "" : "es"}.
              Click "Send Test" to email the student NOW (bypasses the scheduler).
            </p>
          </div>
          {slots.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              No booked classes for today or tomorrow.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {slots.map((slot) => (
                <div
                  key={slot.id}
                  className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
                  data-testid={`reminder-slot-${slot.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate">
                      {slot.student_name || "(no name)"} ↔{" "}
                      {slot.tutor_name || "—"}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {slot.date} at {slot.time} · status: {slot.status}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSendTest(slot)}
                    disabled={sendingId === slot.id}
                    className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0"
                    data-testid={`send-test-${slot.id}`}
                  >
                    {sendingId === slot.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    ) : (
                      <Send className="w-4 h-4 mr-1.5" />
                    )}
                    Send Test
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent reminders sent */}
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="font-semibold text-slate-900">
              Recently sent reminders (last 10)
            </p>
            <p className="text-xs text-slate-500 mt-1">
              From the notifications_sent log — confirms which reminders the
              system already fired.
            </p>
          </div>
          {(!status?.recent_sent || status.recent_sent.length === 0) ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No reminders sent yet.
            </div>
          ) : (
            <div className="divide-y divide-slate-100 text-sm">
              {status.recent_sent.map((r, i) => (
                <div key={i} className="px-5 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">
                      {r.kind?.replace("class_reminder_", "")} · slot{" "}
                      <code className="text-xs bg-slate-100 px-1 rounded">
                        {r.meta?.slot_id?.slice(0, 8) || "—"}
                      </code>
                    </p>
                    <p className="text-xs text-slate-500">{fmtDate(r.sent_at)}</p>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
